import {Component, NgZone, OnInit} from '@angular/core';
import {NzMessageService} from 'ng-zorro-antd/message';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup} from '@angular/forms';
import {SalesPortalService} from 'src/app/services/sales-portal.service';
import {
  TimeMode,
  SplittingMethod,
} from 'src/app/pages/sales-portal/product-group-table/product-group-table.component';
import {NzModalService} from 'ng-zorro-antd/modal';

@Component({
  selector: 'turnover-setting',
  templateUrl: './turnover-setting.component.html',
  styleUrls: ['./turnover-setting.component.less'],
})
export class TurnoverSettingComponent implements OnInit {
  constructor(
    private message: NzMessageService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private zone: NgZone,
    public salesPortalService: SalesPortalService,
    private modal: NzModalService
  ) {
  }

  editStatus: boolean = false;
  editTargetProcurement: boolean = false;
  nextStepStatus: TimeMode = '';
  splittingMethod: SplittingMethod = 'manualSplitting';
  form!: FormGroup;
  time: TimeMode = '';
  isSpinning: boolean = false;
  detailInfo: any = {};

  get remaining() {
    const targetTurnover = this.detailInfo.targetProcurement;
    if (
      Array.isArray(this.detailInfo.targetClassificationList) &&
      this.detailInfo.targetClassificationList.length > 0
    ) {
      let sum = this.detailInfo.targetClassificationList.reduce(
        (total: any, group: { targetAmount: any }) => {
          return total + group.targetAmount;
        },
        0
      );
      return Number((targetTurnover - sum)?.toFixed(2));
    } else {
      return 0;
    }
  }

  get nextRemaining() {
    const targetTurnover = this.detailInfo.targetProcurement;
    if (
      Array.isArray(this.detailInfo.targetClassificationList) &&
      this.detailInfo.targetClassificationList.length > 0
    ) {
      let sum = this.detailInfo.targetClassificationList.reduce(
        (
          total: any,
          group: {
            targetConfigurationList: any[];
          }
        ) => {
          let groupSum = group.targetConfigurationList.reduce(
            (groupTotal, quarter) => {
              return groupTotal + quarter.targetAmount;
            },
            0
          );
          return total + groupSum;
        },
        0
      );
      return Number((targetTurnover - sum)?.toFixed(2));
    } else {
      return 0;
    }
  }

  ngOnInit(): void {
    document.body.classList.add('sales-portal-body');
    this.form = this.fb.group({
      itemsFormArray: this.fb.array([]),
    });

    this.route.queryParams.subscribe((params) => {
      const query = {
        targetYear: params['targetYear'],
        dealerCode: params['dealerCode'],
        dealerOrgCode: params['dealerOrgCode'],
        dealerChannelCode: params['dealerChannelCode'],
        viewFlag: false
      };
      this.getDetail(query);
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('sales-portal-body');
  }

  getDetail(params: object) {
    this.isSpinning = true;
    this.salesPortalService.turnoverSplitDetail(params).subscribe(
      (result: any) => {
        const {rtnCode, rtnData} = result;
        if (rtnCode === 10000) {
          this.detailInfo = rtnData;
        }
        this.isSpinning = false;
      },
      () => {
        this.isSpinning = false;
      }
    );
  }

  navigate(path: string): void {
    this.router.navigate([path]);
  }

  handleEdit() {
    this.editStatus = true;
    if (this.detailInfo.classificationMethod === 'PACKAGE') {
      this.nextStepStatus = this.detailInfo.targetSplitMethod;
      this.time = this.detailInfo.targetSplitMethod;
    }
  }

  handleNextStepCancel() {
    this.nextStepStatus = '';
    this.editStatus = false;
  }

  handleNextStep() {
    if (this.remaining !== 0) {
      this.message.create('warning', 'Remaining must be equal to 0');
    } else {
      this.modal.confirm({
        nzTitle: '<span>Change Reminder</span>',
        nzContent:
          '<span>After modifying the product group configuration, the historical time configuration will be cleared.</span><br><span>Are you sure you wish to continue this operation?</span>',
        nzOkText: 'Yes',
        nzWidth: 540,
        nzIconType: '',
        nzClassName: 'sales-confirm-modal',
        nzOnOk: () => {
          this.nextStepStatus = this.detailInfo.targetSplitMethod;
          this.time = this.detailInfo.targetSplitMethod;
        },
      });
    }
  }

  handleLastStep() {
    this.nextStepStatus = '';
  }

  handleCancel() {
    this.editStatus = false;
    this.nextStepStatus = '';
  }

  handleSave() {
    this.isSpinning = true;
    if (this.detailInfo.classificationMethod === 'PACKAGE') {
      this.detailInfo.targetClassificationList[0].targetAmount =
        this.detailInfo.targetProcurement;
    }
    this.salesPortalService.saveTargetClassification(this.detailInfo).subscribe(
      (result: any) => {
        const {rtnCode} = result;
        if (rtnCode === 10000) {
          this.router.navigate(['/sales-portal/turnover']);
        }
        this.isSpinning = false;
      },
      () => {
        this.isSpinning = false;
      }
    );
  }

  handleSplittingMethodChange() {
    this.handleProductDividedOnNextStatusNull();
  }

  handleTimeChange() {
    this.zone.run(() => {
      this.detailInfo.targetSplitMethod = this.time;
      if (this.time === 'BY_MONTH') {
        this.handleChangeToMonth();
      } else if (this.time === 'BY_QUARTER') {
        this.handleChangeToQuarter();
      } else if (this.time === 'BY_YEAR') {
        this.handleChangeToYear();
      }
    });
  }

  handleChangeToMonth() {
    this.detailInfo.targetClassificationList =
      this.detailInfo.targetClassificationList.map(
        (targetClassificationItem: {
          targetConfigurationList: {
            name: string;
            targetAmount: number;
            actualAmount: number;
          }[];
        }) => {
          targetClassificationItem.targetConfigurationList =
            this.salesPortalService.MONTH_ARRAY.map((monthItem) => {
              return {
                name: monthItem,
                targetAmount: 0,
                actualAmount: 0,
              };
            });
          return targetClassificationItem;
        }
      );
    this.nextStepStatus = 'BY_MONTH';
  }

  handleChangeToQuarter() {
    this.detailInfo.targetClassificationList =
      this.detailInfo.targetClassificationList.map(
        (targetClassificationItem: {
          targetConfigurationList: {
            name: string;
            targetAmount: number;
            actualAmount: number;
          }[];
        }) => {
          targetClassificationItem.targetConfigurationList =
            this.salesPortalService.QUARTER_ARRAY.map((quarterItem) => {
              return {
                name: quarterItem,
                targetAmount: 0,
                actualAmount: 0,
              };
            });
          return targetClassificationItem;
        }
      );
    this.nextStepStatus = 'BY_QUARTER';
  }

  handleChangeToYear() {
    this.detailInfo.targetClassificationList =
      this.detailInfo.targetClassificationList.map(
        (targetClassificationItem: {
          targetConfigurationList: {
            name: string;
            targetAmount: number;
            actualAmount: number;
          }[];
        }) => {
          targetClassificationItem.targetConfigurationList = [
            this.detailInfo.targetYear,
          ].map((yearItem) => {
            return {
              name: yearItem,
              targetAmount: 0,
              actualAmount: 0,
            };
          });
          return targetClassificationItem;
        }
      );
    this.nextStepStatus = 'BY_YEAR';
  }

  handleProductDividedOnNextStatusNull() {
    if (
      this.splittingMethod === 'automaticallySplitting' &&
      this.nextStepStatus === ''
    ) {
      const totalNum = this.detailInfo.targetClassificationList.length;
      const dividedData = Number(
        (this.detailInfo.targetProcurement / totalNum)?.toFixed(2)
      );
      const remainData = Number(
        (
          this.detailInfo.targetProcurement -
          dividedData * (totalNum - 1)
        )?.toFixed(2)
      );

      this.detailInfo.targetClassificationList =
        this.detailInfo.targetClassificationList.map(
          (item: { targetAmount: number }, index: number) => {
            if (index < totalNum - 1) {
              item.targetAmount = dividedData;
            } else {
              item.targetAmount = remainData;
            }
            return item;
          }
        );
    }
  }
}
